import React, {
  createContext,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getLoginToken,
  getStoredUser,
  getTourCompletion,
  setStoredUser,
} from "../storage";
import { getDecodedJWT, isAuthenticated } from "../utils";
import { useAuthenticatedUser } from "./hooks";
import { hooks } from "../hooks";
import { ChildProps, userProps } from "../interface";

export interface AuthContextType {
  user?: userProps;
  token?: string;
  isAuthenticated: boolean;
  authenticate(token: string): void;
  logout(): void;
  updateUser(data: userProps): void;
  toggleDrawer(): void;
  open: boolean;
  isTourComplete: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: undefined,
  token: undefined,
  isAuthenticated: false,
  authenticate: () => {}, // no-op
  logout: () => {}, // no-op
  updateUser: () => {}, // no-op
  toggleDrawer: () => {}, // no-op
  open: false,
  isTourComplete: false,
});

function AuthContextProvider({ children }: ChildProps) {
  const [authToken, setAuthToken] = useState<string | undefined>();
  const [user, setUser] = useState<userProps | undefined>();
  const userDetails = useAuthenticatedUser();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { mutate: logoutMutate, isSuccess, reset } = hooks.useLogout();
  const [isTourComplete, setTourComplete] = useState<boolean>(false);

  // If the token has expired or is missing, force logout
  useEffect(() => {
    if (!isAuthenticated()) {
      logout();
    }
  }, []);

  // On mount, load token & user from storage
  useEffect(() => {
    const token = getLoginToken();
    if (token) setAuthToken(token);
  }, []);

  useEffect(() => {
    const stored = getStoredUser();
    if (stored) setUser(stored);
  }, []);

  // Sync in case useAuthenticatedUser hook returns fresh data
  useEffect(() => {
    if (userDetails) {
      setUser(userDetails);
    }
  }, [userDetails]);

  // syncing tour completion state
  useEffect(() => {
    const isTourComplete: boolean = getTourCompletion();
    setTourComplete(isTourComplete);
  }, []);

  function logout() {
    logoutMutate();
  }

  if (isSuccess) {
    reset();
    localStorage.clear();
    setAuthToken(undefined);
    setUser(undefined);
    queryClient.clear();
  }
  function updateUser(data: userProps) {
    setUser(data);
    setStoredUser(data);
  }

  function authenticate(token: string) {
    setAuthToken(token);

    // decode & shape user
    const decoded: any = getDecodedJWT();
    const u: userProps = {
      _id: decoded._id || "",
      firstname: decoded.firstname || "",
      lastname: decoded.lastname || "",
      middlename: "",
      fullname: "",
      phone: "",
      email: decoded.email || "",
      role: decoded.role || "",
      isInvited: decoded.isInvited || false,
      isInvitedPasswordUpdate: decoded.isInvitedPasswordUpdate || false,
      isPaid: decoded.isPaid || false,
    };

    setUser(u);
    setStoredUser(u);
  }

  function toggleDrawer() {
    setOpen((o) => !o);
  }

  const value: AuthContextType = {
    user,
    token: authToken,
    isAuthenticated: Boolean(authToken),
    authenticate,
    logout,
    updateUser,
    toggleDrawer,
    open,
    isTourComplete,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContextProvider;
